import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";

const AUTO_ROTATE_INTERVAL = 5000; // 5 seconds

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return "0";
  }

  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);
};

const round = (value, decimals = 2) => {
  const numberValue = Number(value);

  if (!Number.isFinite(numberValue)) {
    return 0;
  }

  const factor = Math.pow(10, decimals);

  return Math.round(numberValue * factor) / factor;
};

function ProductionMixChart({
  series = [],
  totalProduced = 0,
  chartKey = "",
}) {
  const [activeMetric, setActiveMetric] = useState("ok");

  const productionData = useMemo(() => {
    const values = Array.isArray(series)
      ? series.map((value) => Number(value || 0))
      : [0, 0, 0];

    const okQty = values[0] || 0;
    const rejectQty = values[1] || 0;
    const reworkQty = values[2] || 0;

    const calculatedTotal =
      Number(totalProduced) > 0
        ? Number(totalProduced)
        : okQty + rejectQty + reworkQty;

    const percentage = (value) => {
      if (calculatedTotal <= 0) {
        return 0;
      }

      return round(
        (Number(value || 0) / calculatedTotal) * 100,
        2,
      );
    };

    return {
      totalProduced: calculatedTotal,

      okQty,
      rejectQty,
      reworkQty,

      okPercentage: percentage(okQty),
      rejectPercentage: percentage(rejectQty),
      reworkPercentage: percentage(reworkQty),
    };
  }, [series, totalProduced]);

  const metrics = useMemo(() => {
    return [
      {
        key: "ok",
        label: "OK",
        fullLabel: "OK Quantity",
        value: productionData.okQty,
        percentage: productionData.okPercentage,
        color: "#16a34a",
        gradientTo: "#4ade80",
      },

      {
        key: "reject",
        label: "Reject",
        fullLabel: "Reject Quantity",
        value: productionData.rejectQty,
        percentage: productionData.rejectPercentage,
        color: "#ef4444",
        gradientTo: "#f87171",
      },

      {
        key: "rework",
        label: "Rework",
        fullLabel: "Rework Quantity",
        value: productionData.reworkQty,
        percentage: productionData.reworkPercentage,
        color: "#f59e0b",
        gradientTo: "#fbbf24",
      },
    ];
  }, [productionData]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveMetric((currentMetric) => {
        const currentIndex = metrics.findIndex(
          (item) => item.key === currentMetric,
        );

        const nextIndex =
          currentIndex >= 0
            ? (currentIndex + 1) % metrics.length
            : 0;

        return metrics[nextIndex].key;
      });
    }, AUTO_ROTATE_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [metrics]);

  const selectedMetric = useMemo(() => {
    return (
      metrics.find((item) => item.key === activeMetric) ||
      metrics[0]
    );
  }, [metrics, activeMetric]);

  const chartSeries = useMemo(() => {
    return [selectedMetric.percentage];
  }, [selectedMetric]);

  const options = useMemo(() => {
    return {
      chart: {
        type: "radialBar",

        background: "transparent",

        animations: {
          enabled: true,

          easing: "easeinout",

          speed: 1100,

          animateGradually: {
            enabled: true,
            delay: 120,
          },

          dynamicAnimation: {
            enabled: true,
            speed: 650,
          },
        },

        toolbar: {
          show: false,
        },

        sparkline: {
          enabled: false,
        },

        parentHeightOffset: 0,

        fontFamily:
          'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      },

      colors: [selectedMetric.color],

      labels: [selectedMetric.fullLabel],

      plotOptions: {
        radialBar: {
          startAngle: -135,

          endAngle: 135,

          hollow: {
            margin: 0,

            size: "58%",

            background: "#ffffff",

            position: "front",

            dropShadow: {
              enabled: true,

              top: 4,

              left: 0,

              blur: 18,

              opacity: 0.08,
            },
          },

          track: {
            background: "#edf2f7",

            strokeWidth: "100%",

            margin: 0,

            dropShadow: {
              enabled: false,
            },
          },

          dataLabels: {
            show: true,

            name: {
              show: true,

              offsetY: -19,

              color: "#64748b",

              fontSize: "12px",

              fontWeight: 700,
            },

            value: {
              show: true,

              offsetY: 9,

              color: selectedMetric.color,

              fontSize: "34px",

              fontWeight: 900,

              formatter: () => {
                return `${round(
                  selectedMetric.percentage,
                  2,
                )}%`;
              },
            },
          },
        },
      },

      stroke: {
        lineCap: "round",
      },

      fill: {
        type: "gradient",

        gradient: {
          shade: "light",

          type: "vertical",

          shadeIntensity: 0.25,

          inverseColors: false,

          gradientToColors: [
            selectedMetric.gradientTo,
          ],

          opacityFrom: 1,

          opacityTo: 0.9,

          stops: [0, 60, 100],
        },
      },

      tooltip: {
        enabled: true,

        custom: () => {
          return `
            <div class="production-focus-tooltip">
              <div class="production-focus-tooltip-title">
                <span
                  class="production-focus-tooltip-dot"
                  style="background:${selectedMetric.color}"
                ></span>

                <span>
                  ${selectedMetric.fullLabel}
                </span>
              </div>

              <strong>
                ${formatNumber(selectedMetric.value)}
              </strong>

              <small>
                ${round(
                  selectedMetric.percentage,
                  2,
                )}% of total production
              </small>
            </div>
          `;
        },
      },

      states: {
        normal: {
          filter: {
            type: "none",
          },
        },

        hover: {
          filter: {
            type: "lighten",
            value: 0.04,
          },
        },

        active: {
          filter: {
            type: "none",
          },
        },
      },

      noData: {
        text: "No production mix data",
      },
    };
  }, [selectedMetric]);

  return (
    <div
      className="production-mix-focus"
      key={`production-mix-${chartKey}`}
    >
      <div className="production-mix-selector">
        {metrics.map((item, index) => (
          <button
            key={item.key}
            type="button"
            className={`production-mix-selector-item ${item.key} ${
              activeMetric === item.key ? "active" : ""
            }`}
            style={{
              "--metric-color": item.color,
              "--delay": `${index * 100}ms`,
            }}
            onClick={() => setActiveMetric(item.key)}
          >
            <span className="production-mix-selector-top">
              <span className="production-mix-selector-dot" />

              <span className="production-mix-selector-label">
                {item.label}
              </span>
            </span>

            <strong>
              {formatNumber(item.value)}
            </strong>

            <small>
              {round(item.percentage, 2)}%
            </small>
          </button>
        ))}
      </div>

      <div className="production-focus-content">
        <div className="production-focus-chart">
          <Chart
            key={`${chartKey}-${activeMetric}`}
            options={options}
            series={chartSeries}
            type="radialBar"
            height={330}
          />
        </div>

        <div
          className="production-focus-details"
          key={`details-${activeMetric}`}
          style={{
            "--metric-color": selectedMetric.color,
          }}
        >
          <span className="production-focus-badge">
            <span className="production-focus-badge-dot" />

            Selected Metric
          </span>

          <h4>
            {selectedMetric.fullLabel}
          </h4>

          <strong className="production-focus-main-value">
            {formatNumber(selectedMetric.value)}
          </strong>

          <div className="production-focus-percentage-row">
            <span>
              {round(
                selectedMetric.percentage,
                2,
              )}
              %
            </span>

            <small>
              of total production
            </small>
          </div>

          <div className="production-focus-total">
            <span>
              Total Produced
            </span>

            <strong>
              {formatNumber(
                productionData.totalProduced,
              )}
            </strong>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductionMixChart;